import { useTranslation } from 'react-i18next';
import { ColumnDef } from '@tanstack/react-table';
import { TableCell, ColumnCustomiser } from '@anytag/shared-atoms';
import { usePostDetailsParam, useToggleTableColumns } from '@anytag/shared-hooks';
import { formatIntNumber, formatNumber, formatPercent, formatPrice } from '@anytag/shared-utils';
import {
  CampaignType,
  CampaignSocialMediaType,
  CampaignReportOrderField,
  MarketplaceCampaignReport
} from '@anytag/graphql';
import { Post } from '../Post';
import { InfluencerPost } from '../InfluencerPost';
import { usePostReportFilter } from '../../PostReportFilter';
import { usePostReportContext } from '../../PostReportContext';
import { PostReportRowData, PostReportTableStatKeys } from '../types';
import { EngagementCampaignTrafficSource } from '../../../EngagementCampaigns';

interface Props {
  data: MarketplaceCampaignReport;
}

export const usePostReportTable = ({ data }: Props) => {
  const { t } = useTranslation();
  const { filter } = usePostReportFilter();
  const { setPostDetailsParam } = usePostDetailsParam();
  const { hasTracking, campaignType, isOnlyTikTokTtcm } = usePostReportContext();
  const { getSelectableColumns, handleToggleColumns } = useToggleTableColumns<
    PostReportRowData,
    PostReportTableStatKeys
  >();

  const { socialMedias, id: campaignId, hasTrafficSource } = data.campaignInfo;
  const currency = t(data.currency || '');
  const isEngagement = campaignType === CampaignType.ENGAGEMENT;
  const hasXhs = socialMedias.includes(CampaignSocialMediaType.XHS);
  const hasTiktok = socialMedias.includes(CampaignSocialMediaType.TIKTOK);
  const hasDouyin = socialMedias.includes(CampaignSocialMediaType.DOUYIN);
  const hasThreads = socialMedias.includes(CampaignSocialMediaType.THREADS);
  const hasTwitter = socialMedias.includes(CampaignSocialMediaType.TWITTER);
  const hasYoutube = socialMedias.includes(CampaignSocialMediaType.YOUTUBE);
  const hasFacebook = socialMedias.includes(CampaignSocialMediaType.FACEBOOK);
  const hasIgFeed = socialMedias.includes(CampaignSocialMediaType.INSTAGRAM);
  const hasIgStory = socialMedias.includes(CampaignSocialMediaType.INSTAGRAM_STORY);
  const hasInstagram = hasIgFeed || hasIgStory;
  const hasIgOrThreads = hasInstagram || hasThreads;
  const hasOnlyTiktok = socialMedias.length === 1 && hasTiktok;
  const hasOnlyTwitter = socialMedias.length === 1 && hasTwitter;
  const hasOnlyInstagram =
    socialMedias.length > 0 &&
    socialMedias.every((sm) =>
      [CampaignSocialMediaType.INSTAGRAM, CampaignSocialMediaType.INSTAGRAM_STORY].includes(sm)
    );
  const hasFbReactions = socialMedias[0] === CampaignSocialMediaType.FACEBOOK;

  const columns: ColumnDef<PostReportRowData>[] = [
    {
      enableHiding: false,
      accessorKey: 'title',
      meta: { sticky: true },
      header: ({ table }) => {
        const { selectableOptions } = getSelectableColumns(table);

        return (
          <div css={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            {t('HeaderColumn.Total Post')}
            {hasTrafficSource ? (
              <EngagementCampaignTrafficSource
                css={{ margin: '0 8px 0 auto' }}
                campaignId={campaignId}
                filter={filter}
              />
            ) : null}
            <ColumnCustomiser
              options={selectableOptions}
              onChange={handleToggleColumns(table)}
              savedValuesKey={isEngagement ? 'engagementReportTableColumns' : 'marketplaceReportTableColumns'}
              buttonProps={{
                tooltip: { place: 'top', help: <span className="text-sm">{t('Button.Customise report table')}</span> }
              }}
            />
          </div>
        );
      },
      cell: ({ row }) => {
        const { date, className, influencerInfo, postInfo } = row.original;

        return className === 'total-row' ? (
          <TableCell.CampaignTotal count={data.postCount} unit={t('Post')} />
        ) : className === 'average-row' ? (
          <TableCell.CampaignAverage title={t('Average')} />
        ) : date ? (
          <TableCell.Text className="text-right" value={date} />
        ) : influencerInfo ? (
          <InfluencerPost isEngagement={isEngagement} influencerInfo={influencerInfo} />
        ) : postInfo ? (
          <div className="flex items-center gap-2">
            <div className="flex-4" />
            <Post
              url={postInfo.postUrl}
              content={postInfo.content}
              isEngagement={isEngagement}
              socialMedia={postInfo.socialMedia}
              thumbNail={postInfo.thumbNail || ''}
              hasTrafficSource={!!postInfo.hasTrafficSource}
              onClickPost={() => setPostDetailsParam(postInfo.id)}
            />
          </div>
        ) : null;
      }
    },
    {
      accessorKey: 'cost',
      enableHiding: false,
      header: t<string>('HeaderColumn.Cost'),
      meta: {
        align: 'right',
        help: t<string>('Tooltip.costHelp'),
        sortField: CampaignReportOrderField.COST
      },
      cell: ({ row }) => (
        <TableCell.Number
          unit={row.original.stats.cost ? currency : ''}
          value={formatPrice(row.original.stats.cost || null, currency)}
        />
      )
    },
    {
      accessorKey: 'followers',
      enableHiding: false,
      header: t<string>('HeaderColumn.Follower'),
      meta: {
        align: 'right',
        help: t<string>('Tooltip.UniqueReachHelp'),
        sortField: CampaignReportOrderField.FOLLOWERS
      },
      cell: ({ row }) => <TableCell.Number value={formatIntNumber(row.original.stats.followers)} />
    },
    {
      accessorKey: 'followerReach',
      header: t<string>('HeaderColumn.Follower Reach'),
      meta: {
        align: 'right',
        help: t<string>('Tooltip.followerReachHelp'),
        sortField: CampaignReportOrderField.FOLLOWER_REACH,
        hidden: !isEngagement || !hasInstagram
      },
      cell: ({ row }) => <TableCell.Number value={formatIntNumber(row.original.stats.followerReach)} />
    },
    {
      accessorKey: 'followerReachRate',
      header: t<string>('HeaderColumn.Follower Reach Rate'),
      meta: {
        align: 'right',
        help: t<string>('Tooltip.followerReachRateHelp'),
        sortField: CampaignReportOrderField.FOLLOWER_REACH_RATE,
        hidden: !isEngagement || !hasInstagram
      },
      cell: ({ row }) => (
        <TableCell.Number value={formatPercent(row.original.stats.followerReachRate, false)} unit="%" />
      )
    },
    {
      accessorKey: 'newFollowers',
      header: t<string>('HeaderColumn.New followers'),
      meta: {
        align: 'right',
        help: t<string>('Tooltip.newFollowersHelp'),
        sortField: CampaignReportOrderField.NEW_FOLLOWERS,
        hidden: !isEngagement || !hasTwitter
      },
      cell: ({ row }) => <TableCell.Number value={formatIntNumber(row.original.stats.newFollowers)} />
    },
    {
      accessorKey: 'impressions',
      header: t<string>('HeaderColumn.Impression'),
      meta: {
        align: 'right',
        help: t<string>('Tooltip.impressionsHelp'),
        sortField: CampaignReportOrderField.IMPRESSIONS,
        hidden: isOnlyTikTokTtcm || (isEngagement && (hasOnlyInstagram || hasOnlyTiktok))
      },
      cell: ({ row }) => <TableCell.Number value={formatIntNumber(row.original.stats.impressions)} />
    },
    {
      accessorKey: 'impressionRate',
      header: t<string>('HeaderColumn.Impression Rate'),
      meta: {
        align: 'right',
        help: t<string>('Tooltip.impressionsRateHelp'),
        sortField: CampaignReportOrderField.IMPRESSION_RATE,
        hidden: isEngagement && (hasOnlyInstagram || hasOnlyTiktok)
      },
      cell: ({ row }) => <TableCell.Number value={formatPercent(row.original.stats.impressionRate, false)} unit="%" />
    },
    {
      accessorKey: 'impressionsClickThroughRate',
      header: t<string>('HeaderColumn.Impressions click through rate'),
      meta: {
        align: 'right',
        help: t<string>('Tooltip.impressionsClickThroughRateHelp'),
        sortField: CampaignReportOrderField.IMPRESSIONS_CLICK_THROUGH_RATE,
        hidden: !isEngagement || !hasYoutube
      },
      cell: ({ row }) => (
        <TableCell.Number value={formatPercent(row.original.stats.impressionsClickThroughRate, false)} unit="%" />
      )
    },
    {
      accessorKey: 'impressionsFromDiscovery',
      header: t<string>('HeaderColumn.Impression from Discovery'),
      meta: {
        align: 'right',
        help: t<string>('Tooltip.impressionsFromDiscoveryHelp'),
        sortField: CampaignReportOrderField.IMPRESSIONS_FROM_DISCOVERY,
        hidden: !isEngagement || !hasThreads
      },
      cell: ({ row }) => <TableCell.Number value={formatIntNumber(row.original.stats.impressionsFromDiscovery)} />
    },
    {
      accessorKey: 'impressionsFromHashtag',
      header: t<string>('HeaderColumn.Impression from Hashtag'),
      meta: {
        align: 'right',
        help: t<string>('Tooltip.impressionsFromHashtagHelp'),
        sortField: CampaignReportOrderField.IMPRESSIONS_FROM_HASHTAG,
        hidden: !isEngagement || !hasThreads
      },
      cell: ({ row }) => <TableCell.Number value={formatIntNumber(row.original.stats.impressionsFromHashtag)} />
    },
    {
      accessorKey: 'impressionsFromHome',
      header: t<string>('HeaderColumn.Impression from Home'),
      meta: {
        align: 'right',
        help: t<string>('Tooltip.impressionsFromHomeHelp'),
        sortField: CampaignReportOrderField.IMPRESSIONS_FROM_HOME,
        hidden: isEngagement && !hasThreads
      },
      cell: ({ row }) => <TableCell.Number value={formatIntNumber(row.original.stats.impressionsFromHome)} />
    },
    {
      accessorKey: 'impressionsFromProfile',
      header: t<string>('HeaderColumn.Impression from Profile'),
      meta: {
        align: 'right',
        help: t<string>('Tooltip.impressionsFromProfileHelp'),
        sortField: CampaignReportOrderField.IMPRESSIONS_FROM_PROFILE,
        hidden: isEngagement && !hasThreads
      },
      cell: ({ row }) => <TableCell.Number value={formatIntNumber(row.original.stats.impressionsFromProfile)} />
    },
    {
      accessorKey: 'impressionsFromOther',
      header: t<string>('HeaderColumn.Impression from Other'),
      meta: {
        align: 'right',
        help: t<string>('Tooltip.impressionsFromOtherHelp'),
        sortField: CampaignReportOrderField.IMPRESSIONS_FROM_OTHER,
        hidden: isEngagement && !hasThreads
      },
      cell: ({ row }) => <TableCell.Number value={formatIntNumber(row.original.stats.impressionsFromOther)} />
    },
    {
      accessorKey: 'totalPlayTime',
      header: t<string>('HeaderColumn.Total play time'),
      meta: {
        align: 'right',
        help: t<string>('Tooltip.totalPlayTimeHelp'),
        sortField: CampaignReportOrderField.TOTAL_PLAY_TIME,
        hidden: !isOnlyTikTokTtcm
      },
      cell: ({ row }) => <TableCell.Number value={formatIntNumber(row.original.stats.totalPlayTime)} />
    },
    {
      accessorKey: 'avgPlayRate',
      header: t<string>('HeaderColumn.Average play rate'),
      meta: {
        align: 'right',
        help: t<string>('Tooltip.avgPlayRateHelp'),
        sortField: CampaignReportOrderField.AVG_PLAY_RATE,
        hidden: !isEngagement || !hasYoutube
      },
      cell: ({ row }) => <TableCell.Number value={formatPercent(row.original.stats.avgPlayRate, false)} unit="%" />
    },
    {
      accessorKey: 'averageViewTime',
      header: t<string>('HeaderColumn.Average view time'),
      meta: {
        align: 'right',
        help: t<string>('Tooltip.averageViewTimeHelp'),
        sortField: CampaignReportOrderField.AVERAGE_VIEW_TIME,
        hidden: !isOnlyTikTokTtcm
      },
      cell: ({ row }) => <TableCell.Number value={formatNumber(row.original.stats.averageViewTime, 2)} />
    },
    {
      accessorKey: 'videoCompletionRate',
      header: t<string>('HeaderColumn.Video completion rate'),
      meta: {
        align: 'right',
        help: t<string>('Tooltip.videoCompletionRateHelp'),
        sortField: CampaignReportOrderField.VIDEO_COMPLETION_RATE,
        hidden: !isOnlyTikTokTtcm
      },
      cell: ({ row }) => (
        <TableCell.Number value={formatPercent(row.original.stats.videoCompletionRate, false)} unit="%" />
      )
    },
    {
      accessorKey: 'view',
      header: t<string>(isEngagement && (hasOnlyInstagram || hasXhs) ? 'HeaderColumn.View' : 'HeaderColumn.Video view'),
      meta: {
        align: 'right',
        sortField: CampaignReportOrderField.VIEW,
        help: t<string>(isEngagement && (hasOnlyInstagram || hasXhs) ? 'Tooltip.ViewHelp' : 'Tooltip.videoViewHelp')
      },
      cell: ({ row }) => <TableCell.Number value={formatIntNumber(row.original.stats.view)} />
    },
    {
      accessorKey: 'viewRate',
      header: t<string>('HeaderColumn.Video view rate'),
      meta: {
        align: 'right',
        help: t<string>('Tooltip.videoViewRateHelp'),
        sortField: CampaignReportOrderField.VIEW_RATE,
        hidden: !isEngagement || !(hasFacebook || hasTiktok)
      },
      cell: ({ row }) => <TableCell.Number value={formatPercent(row.original.stats.viewRate, false)} unit="%" />
    },
    {
      accessorKey: 'audienceRetention',
      header: t<string>('HeaderColumn.Audience Retention Rate'),
      meta: {
        align: 'right',
        help: t<string>('Tooltip.audienceRetentionHelp'),
        sortField: CampaignReportOrderField.AUDIENCE_RETENTION,
        hidden: !isEngagement || !hasYoutube
      },
      cell: ({ row }) => (
        <TableCell.Number value={formatPercent(row.original.stats.audienceRetention, false)} unit="%" />
      )
    },
    {
      accessorKey: 'totalWatchTimes',
      header: t<string>('HeaderColumn.Total Watch Time'),
      meta: {
        align: 'right',
        help: t<string>('Tooltip.totalWatchTimeHelp'),
        sortField: CampaignReportOrderField.TOTAL_WATCH_TIMES,
        hidden: !isEngagement || !hasTiktok
      },
      cell: ({ row }) => <TableCell.Number value={formatIntNumber(row.original.stats.totalWatchTimes)} />
    },
    {
      accessorKey: 'avgWatchTimes',
      header: t<string>('HeaderColumn.Average Watch Time'),
      meta: {
        align: 'right',
        help: t<string>('Tooltip.avgWatchTimesHelp'),
        sortField: CampaignReportOrderField.AVG_WATCH_TIMES,
        hidden: !isEngagement || !hasYoutube
      },
      cell: ({ row }) => <TableCell.Number value={formatIntNumber(row.original.stats.avgWatchTimes)} />
    },
    {
      accessorKey: 'averageViewDurationMinutes',
      header: t<string>('HeaderColumn.Average View Duration (m)'),
      meta: {
        align: 'right',
        help: t<string>('Tooltip.averageViewDurationMinutesHelp'),
        sortField: CampaignReportOrderField.AVERAGE_VIEW_DURATION_MINUTES,
        hidden: !isEngagement || !(hasYoutube || hasThreads || hasTiktok)
      },
      cell: ({ row }) => <TableCell.Number value={formatIntNumber(row.original.stats.averageViewDurationMinutes)} />
    },
    {
      accessorKey: 'averageViewDurationPercentage',
      header: t<string>('HeaderColumn.Average View Duration Rate'),
      meta: {
        align: 'right',
        help: t<string>('Tooltip.averageViewDurationPercentageHelp'),
        sortField: CampaignReportOrderField.AVERAGE_VIEW_DURATION_PERCENTAGE,
        hidden: !isEngagement || !(hasYoutube || hasThreads)
      },
      cell: ({ row }) => (
        <TableCell.Number value={formatPercent(row.original.stats.averageViewDurationPercentage, false)} unit="%" />
      )
    },
    {
      accessorKey: 'fullVideoViews',
      header: t<string>('HeaderColumn.Full Video View'),
      meta: {
        align: 'right',
        help: t<string>('Tooltip.fullVideoViewHelp'),
        sortField: CampaignReportOrderField.FULL_VIDEO_VIEWS,
        hidden: !isEngagement || !hasTiktok
      },
      cell: ({ row }) => <TableCell.Number value={formatIntNumber(row.original.stats.fullVideoViews)} />
    },
    {
      accessorKey: 'viewsByFollower',
      header: t<string>('HeaderColumn.Views by Followers'),
      meta: {
        align: 'right',
        hidden: !isEngagement || !hasInstagram,
        help: t<string>('Tooltip.viewsByFollowersHelp'),
        sortField: CampaignReportOrderField.VIEWS_BY_FOLLOWER
      },
      cell: ({ row }) => <TableCell.Number value={formatPercent(row.original.stats.viewsByFollower, false)} unit="%" />
    },
    {
      accessorKey: 'viewsByNonFollower',
      header: t<string>('HeaderColumn.Views by Non-Followers'),
      meta: {
        align: 'right',
        hidden: !isEngagement || !hasInstagram,
        help: t<string>('Tooltip.viewsByNonFollowersHelp'),
        sortField: CampaignReportOrderField.VIEWS_BY_NON_FOLLOWER
      },
      cell: ({ row }) => (
        <TableCell.Number value={formatPercent(row.original.stats.viewsByNonFollower, false)} unit="%" />
      )
    },
    {
      accessorKey: 'viewsFromHome',
      header: t<string>('HeaderColumn.Views from Home'),
      meta: {
        align: 'right',
        help: t<string>('Tooltip.viewsFromHomeHelp'),
        sortField: CampaignReportOrderField.VIEWS_FROM_HOME,
        hidden: !isEngagement || !(hasXhs || hasIgFeed)
      },
      cell: ({ row }) => <TableCell.Number value={formatIntNumber(row.original.stats.viewsFromHome)} />
    },
    {
      accessorKey: 'viewsFromProfile',
      header: t<string>('HeaderColumn.Views from Profile'),
      meta: {
        align: 'right',
        help: t<string>('Tooltip.viewsFromProfileHelp'),
        sortField: CampaignReportOrderField.VIEWS_FROM_PROFILE,
        hidden: !isEngagement || !(hasXhs || hasIgFeed)
      },
      cell: ({ row }) => <TableCell.Number value={formatIntNumber(row.original.stats.viewsFromProfile)} />
    },
    {
      accessorKey: 'viewsFromDiscovery',
      header: t<string>('HeaderColumn.Views from Discovery'),
      meta: {
        align: 'right',
        help: t<string>('Tooltip.viewsFromDiscoveryHelp'),
        sortField: CampaignReportOrderField.VIEWS_FROM_DISCOVERY,
        hidden: !isEngagement || !hasIgFeed
      },
      cell: ({ row }) => <TableCell.Number value={formatIntNumber(row.original.stats.viewsFromDiscovery)} />
    },
    {
      accessorKey: 'viewsFromSearch',
      header: t<string>('HeaderColumn.Views from Search'),
      meta: {
        align: 'right',
        help: t<string>('Tooltip.viewsFromSearchHelp'),
        sortField: CampaignReportOrderField.VIEWS_FROM_SEARCH,
        hidden: !isEngagement || !hasXhs
      },
      cell: ({ row }) => <TableCell.Number value={formatIntNumber(row.original.stats.viewsFromSearch)} />
    },
    {
      accessorKey: 'viewsFromFollowing',
      header: t<string>('HeaderColumn.Views from Following'),
      meta: {
        align: 'right',
        help: t<string>('Tooltip.viewsFromFollowingHelp'),
        sortField: CampaignReportOrderField.VIEWS_FROM_FOLLOWING,
        hidden: !isEngagement || !hasXhs
      },
      cell: ({ row }) => <TableCell.Number value={formatIntNumber(row.original.stats.viewsFromFollowing)} />
    },
    {
      accessorKey: 'viewsFromOther',
      header: t<string>('HeaderColumn.Views from Other'),
      meta: {
        align: 'right',
        help: t<string>('Tooltip.viewsFromOtherHelp'),
        sortField: CampaignReportOrderField.VIEWS_FROM_OTHER,
        hidden: !isEngagement || !(hasXhs || hasIgFeed)
      },
      cell: ({ row }) => <TableCell.Number value={formatIntNumber(row.original.stats.viewsFromOther)} />
    },
    {
      accessorKey: 'profileViewsRate',
      header: t<string>('HeaderColumn.Profile Views Rate'),
      meta: {
        align: 'right',
        help: t<string>('Tooltip.profileViewsRateHelp'),
        sortField: CampaignReportOrderField.PROFILE_VIEWS_RATE,
        hidden: !isEngagement || !hasIgFeed
      },
      cell: ({ row }) => <TableCell.Number value={formatPercent(row.original.stats.profileViewsRate, false)} unit="%" />
    },
    {
      accessorKey: 'storyViewsRate',
      header: t<string>('HeaderColumn.Story Views Rate'),
      meta: {
        align: 'right',
        help: t<string>('Tooltip.storyViewsRateHelp'),
        sortField: CampaignReportOrderField.STORY_VIEWS_RATE,
        hidden: !isEngagement || !hasIgFeed
      },
      cell: ({ row }) => <TableCell.Number value={formatPercent(row.original.stats.storyViewsRate, false)} unit="%" />
    },
    {
      accessorKey: 'feedViewsRate',
      header: t<string>('HeaderColumn.Feed Views Rate'),
      meta: {
        align: 'right',
        help: t<string>('Tooltip.feedViewsRateHelp'),
        sortField: CampaignReportOrderField.FEED_VIEWS_RATE,
        hidden: !isEngagement || !hasIgFeed
      },
      cell: ({ row }) => <TableCell.Number value={formatPercent(row.original.stats.feedViewsRate, false)} unit="%" />
    },
    {
      accessorKey: 'exploreViewsRate',
      header: t<string>('HeaderColumn.Explore Views Rate'),
      meta: {
        align: 'right',
        help: t<string>('Tooltip.exploreViewsRateHelp'),
        sortField: CampaignReportOrderField.EXPLORE_VIEWS_RATE,
        hidden: !isEngagement || !hasIgFeed
      },
      cell: ({ row }) => <TableCell.Number value={formatPercent(row.original.stats.exploreViewsRate, false)} unit="%" />
    },
    {
      accessorKey: 'searchViewsRate',
      header: t<string>('HeaderColumn.Search Views Rate'),
      meta: {
        align: 'right',
        help: t<string>('Tooltip.searchViewsRateHelp'),
        sortField: CampaignReportOrderField.SEARCH_VIEWS_RATE,
        hidden: !isEngagement || !hasIgFeed
      },
      cell: ({ row }) => <TableCell.Number value={formatPercent(row.original.stats.searchViewsRate, false)} unit="%" />
    },
    {
      accessorKey: 'reach',
      header: t<string>('HeaderColumn.Reach'),
      meta: {
        align: 'right',
        help: t<string>('Tooltip.reachHelp'),
        sortField: CampaignReportOrderField.REACH,
        hidden: isEngagement && !(hasIgOrThreads || hasYoutube || hasFacebook)
      },
      cell: ({ row }) => <TableCell.Number value={formatIntNumber(row.original.stats.reach)} />
    },
    {
      accessorKey: 'reachRate',
      header: t<string>('HeaderColumn.Reach Rate'),
      meta: {
        align: 'right',
        help: t<string>('Tooltip.reachRateHelp'),
        sortField: CampaignReportOrderField.REACH_RATE,
        hidden: isEngagement && !(hasIgOrThreads || hasYoutube || hasFacebook)
      },
      cell: ({ row }) => <TableCell.Number value={formatPercent(row.original.stats.reachRate, false)} unit="%" />
    },
    {
      accessorKey: 'nonFollowerReach',
      header: t<string>('HeaderColumn.Non Follower Reach'),
      meta: {
        align: 'right',
        help: t<string>('Tooltip.nonFollowerReachHelp'),
        sortField: CampaignReportOrderField.NON_FOLLOWER_REACH,
        hidden: !isEngagement || !hasIgOrThreads
      },
      cell: ({ row }) => <TableCell.Number value={formatIntNumber(row.original.stats.nonFollowerReach)} />
    },
    {
      accessorKey: 'nonFollowerReachRate',
      header: t<string>('HeaderColumn.Non Follower Reach Rate'),
      meta: {
        align: 'right',
        help: t<string>('Tooltip.nonFollowerReachRateHelp'),
        sortField: CampaignReportOrderField.NON_FOLLOWER_REACH_RATE,
        hidden: !isEngagement || !hasIgOrThreads
      },
      cell: ({ row }) => (
        <TableCell.Number value={formatPercent(row.original.stats.nonFollowerReachRate, false)} unit="%" />
      )
    },
    {
      accessorKey: 'engagement',
      enableHiding: false,
      header: t<string>('HeaderColumn.Engagement'),
      meta: {
        align: 'right',
        help: t<string>('Tooltip.TotalEngagementsHelp'),
        sortField: CampaignReportOrderField.ENGAGEMENT
      },
      cell: ({ row }) => <TableCell.Number value={formatIntNumber(row.original.stats.engagement)} />
    },
    {
      accessorKey: 'engagementRate',
      enableHiding: false,
      header: t<string>('HeaderColumn.Egg Rate by followers'),
      meta: {
        align: 'right',
        help: t<string>('Tooltip.engagementRateHelp'),
        sortField: CampaignReportOrderField.ENGAGEMENT_RATE
      },
      cell: ({ row }) => <TableCell.Number value={formatPercent(row.original.stats.engagementRate, false)} unit="%" />
    },
    {
      accessorKey: 'engagementViewsRate',
      header: t<string>('HeaderColumn.Egg Rate by views'),
      meta: {
        align: 'right',
        help: t<string>('Tooltip.engagementViewsRateHelp'),
        sortField: CampaignReportOrderField.ENGAGEMENT_VIEWS_RATE,
        hidden: !isEngagement && !(hasYoutube || hasTiktok || hasDouyin)
      },
      cell: ({ row }) => (
        <TableCell.Number value={formatPercent(row.original.stats.engagementViewsRate, false)} unit="%" />
      )
    },
    {
      accessorKey: 'like',
      enableHiding: false,
      header: t<string>('HeaderColumn.Like'),
      meta: {
        align: 'right',
        help: t<string>('Tooltip.LikeHelp'),
        sortField: CampaignReportOrderField.LIKE
      },
      cell: ({ row }) => <TableCell.Number value={formatIntNumber(row.original.stats.like)} />
    },
    {
      accessorKey: 'dislikes',
      header: t<string>('HeaderColumn.Dislike'),
      meta: {
        align: 'right',
        help: t<string>('Tooltip.dislikesHelp'),
        sortField: CampaignReportOrderField.DISLIKES,
        hidden: !isEngagement || !hasThreads
      },
      cell: ({ row }) => <TableCell.Number value={formatIntNumber(row.original.stats.dislikes)} />
    },
    {
      accessorKey: 'comment',
      enableHiding: false,
      header: t<string>(
        socialMedias.length === 1 &&
          [CampaignSocialMediaType.INSTAGRAM_STORY, CampaignSocialMediaType.THREADS].includes(
            socialMedias.at(0) as CampaignSocialMediaType
          )
          ? 'HeaderColumn.Replies'
          : 'HeaderColumn.Comment'
      ),
      meta: {
        align: 'right',
        help: t<string>('Tooltip.commentHelp'),
        sortField: CampaignReportOrderField.COMMENT
      },
      cell: ({ row }) => <TableCell.Number value={formatIntNumber(row.original.stats.comment)} />
    },
    {
      accessorKey: 'share',
      header: t<string>('HeaderColumn.Share'),
      meta: {
        align: 'right',
        help: t<string>('Tooltip.shareHelp'),
        sortField: CampaignReportOrderField.SHARE
      },
      cell: ({ row }) => <TableCell.Number value={formatIntNumber(row.original.stats.share)} />
    },
    {
      accessorKey: 'retweets',
      header: t<string>(isEngagement && hasOnlyTwitter ? 'HeaderColumn.Retweets Twitter' : 'HeaderColumn.Retweets'), // Twitter with X in translation
      meta: {
        align: 'right',
        help: t<string>('Tooltip.retweetHelp'),
        sortField: CampaignReportOrderField.SHARE,
        hidden: isEngagement && !(hasThreads || hasTwitter || hasYoutube || hasFacebook)
      },
      cell: ({ row }) => <TableCell.Number value={formatIntNumber(row.original.stats.retweets)} />
    },
    {
      accessorKey: 'saved',
      header: t<string>('HeaderColumn.Save'),
      meta: {
        align: 'right',
        help: t<string>('Tooltip.SaveHelp'),
        sortField: CampaignReportOrderField.SAVED
      },
      cell: ({ row }) => <TableCell.Number value={formatIntNumber(row.original.stats.saved)} />
    },
    {
      accessorKey: 'interaction',
      header: t<string>(
        isEngagement && hasOnlyInstagram ? 'HeaderColumn.Reel interaction' : 'HeaderColumn.Interaction'
      ),
      meta: {
        align: 'right',
        help: t<string>('Tooltip.interactionHelp'),
        sortField: CampaignReportOrderField.INTERACTION,
        hidden: !isEngagement || !hasIgOrThreads
      },
      cell: ({ row }) => <TableCell.Number value={formatIntNumber(row.original.stats.interaction)} />
    },
    {
      accessorKey: 'linkClicks',
      header: t<string>('HeaderColumn.Link Clicks'),
      meta: {
        align: 'right',
        help: t<string>('Tooltip.linkClicksHelp'),
        sortField: CampaignReportOrderField.LINK_CLICKS,
        hidden: !isEngagement || !(hasInstagram || hasThreads || hasFacebook || hasYoutube)
      },
      cell: ({ row }) => <TableCell.Number value={formatIntNumber(row.original.stats.linkClicks)} />
    },
    {
      accessorKey: 'detailExpands',
      header: t<string>('HeaderColumn.Detail Expands'),
      meta: {
        align: 'right',
        help: t<string>('Tooltip.detailExpandsHelp'),
        sortField: CampaignReportOrderField.DETAIL_EXPANDS,
        hidden: !isEngagement || !hasTwitter
      },
      cell: ({ row }) => <TableCell.Number value={formatIntNumber(row.original.stats.detailExpands)} />
    },
    {
      accessorKey: 'stickerTaps',
      header: t<string>('HeaderColumn.Sticker Taps'),
      meta: {
        align: 'right',
        help: t<string>('Tooltip.stickerTapsHelp'),
        sortField: CampaignReportOrderField.STICKER_TAPS,
        hidden: !isEngagement || !hasThreads
      },
      cell: ({ row }) => <TableCell.Number value={formatIntNumber(row.original.stats.stickerTaps)} />
    },
    {
      accessorKey: 'click',
      header: t<string>('HeaderColumn.Click'),
      meta: {
        align: 'right',
        help: t<string>('Tooltip.Click Action Help'),
        sortField: CampaignReportOrderField.CLICK
      },
      cell: ({ row }) => <TableCell.Number value={formatIntNumber(row.original.stats.click)} />
    },
    {
      accessorKey: 'conversion',
      header: t<string>('HeaderColumn.Conversion'),
      meta: {
        align: 'right',
        help: t<string>('Tooltip.Click Conversion Help'),
        sortField: CampaignReportOrderField.CONVERSION,
        hidden: !hasTracking
      },
      cell: ({ row }) => <TableCell.Number value={formatIntNumber(row.original.stats.conversion)} />
    },
    {
      accessorKey: 'sales',
      header: t<string>('HeaderColumn.Sales'),
      meta: {
        align: 'right',
        sortField: CampaignReportOrderField.SALES,
        hidden: !hasTracking || isOnlyTikTokTtcm
      },
      cell: ({ row }) => (
        <TableCell.Number value={formatPrice(row.original.stats.sales || null, currency)} unit={currency} />
      )
    },
    {
      accessorKey: 'femalePercentage',
      header: t<string>('HeaderColumn.Female Rate'),
      meta: {
        align: 'right',
        sortField: CampaignReportOrderField.FEMALE_PERCENTAGE,
        hidden: !isOnlyTikTokTtcm
      },
      cell: ({ row }) => <TableCell.Number value={formatPercent(row.original.stats.femalePercentage, false)} unit="%" />
    },
    {
      accessorKey: 'malePercentage',
      header: t<string>('HeaderColumn.Male Rate'),
      meta: {
        align: 'right',
        sortField: CampaignReportOrderField.MALE_PERCENTAGE,
        hidden: !isOnlyTikTokTtcm
      },
      cell: ({ row }) => <TableCell.Number value={formatPercent(row.original.stats.malePercentage, false)} unit="%" />
    },
    {
      accessorKey: 'age1824Percentage',
      header: t<string>('HeaderColumn.Age (18-24)'),
      meta: {
        align: 'right',
        sortField: CampaignReportOrderField.AGE_18_24_PERCENTAGE,
        hidden: !isOnlyTikTokTtcm
      },
      cell: ({ row }) => (
        <TableCell.Number value={formatPercent(row.original.stats.age1824Percentage, false)} unit="%" />
      )
    },
    {
      accessorKey: 'age2534Percentage',
      header: t<string>('HeaderColumn.Age (25-34)'),
      meta: {
        align: 'right',
        sortField: CampaignReportOrderField.AGE_25_34_PERCENTAGE,
        hidden: !isOnlyTikTokTtcm
      },
      cell: ({ row }) => (
        <TableCell.Number value={formatPercent(row.original.stats.age2534Percentage, false)} unit="%" />
      )
    },
    {
      accessorKey: 'age35UpPercentage',
      header: t<string>('HeaderColumn.Age (35+)'),
      meta: {
        align: 'right',
        sortField: CampaignReportOrderField.AGE_35_UP_PERCENTAGE,
        hidden: !isOnlyTikTokTtcm
      },
      cell: ({ row }) => (
        <TableCell.Number value={formatPercent(row.original.stats.age35UpPercentage, false)} unit="%" />
      )
    },
    {
      accessorKey: 'costsPerSale',
      header: t<string>('HeaderColumn.Commission rate'),
      meta: {
        align: 'right',
        sortField: CampaignReportOrderField.COST_PER_SALE,
        hidden: isOnlyTikTokTtcm
      },
      cell: ({ row }) => (
        <TableCell.Number value={formatPrice(row.original.stats.costsPerSale || null, currency)} unit={currency} />
      )
    },
    {
      accessorKey: 'costsPerFollower',
      header: t<string>('HeaderColumn.Cost / Follower'),
      meta: {
        align: 'right',
        sortField: CampaignReportOrderField.COST_PER_FOLLOWER
      },
      cell: ({ row }) => (
        <TableCell.Number value={formatPrice(row.original.stats.costsPerFollower || null, currency)} unit={currency} />
      )
    },
    {
      accessorKey: 'costsPerImpressions',
      header: t<string>('HeaderColumn.Cost / Impression'),
      meta: {
        align: 'right',
        sortField: CampaignReportOrderField.COST_PER_IMPRESSIONS
      },
      cell: ({ row }) => (
        <TableCell.Number
          value={formatPrice(row.original.stats.costsPerImpressions || null, currency)}
          unit={currency}
        />
      )
    },
    {
      accessorKey: 'costsPerView',
      header: t<string>('HeaderColumn.Cost / Video view'),
      meta: {
        align: 'right',
        sortField: CampaignReportOrderField.COST_PER_VIEW
      },
      cell: ({ row }) => (
        <TableCell.Number value={formatPrice(row.original.stats.costsPerView || null, currency)} unit={currency} />
      )
    },
    {
      accessorKey: 'costsPerReach',
      header: t<string>('HeaderColumn.Cost / Reach'),
      meta: {
        align: 'right',
        sortField: CampaignReportOrderField.COST_PER_REACH,
        hidden: isEngagement && !hasIgOrThreads
      },
      cell: ({ row }) => (
        <TableCell.Number value={formatPrice(row.original.stats.costsPerReach || null, currency)} unit={currency} />
      )
    },
    {
      accessorKey: 'costsPerEngagement',
      header: t<string>('HeaderColumn.Cost / Engagement'),
      meta: {
        align: 'right',
        sortField: CampaignReportOrderField.COST_PER_ENGAGEMENT
      },
      cell: ({ row }) => (
        <TableCell.Number
          value={formatPrice(row.original.stats.costsPerEngagement || null, currency)}
          unit={currency}
        />
      )
    },
    {
      accessorKey: 'costsPerLike',
      header: t<string>(hasFbReactions ? 'HeaderColumn.Cost / Reaction' : 'HeaderColumn.Cost / Like'),
      meta: {
        align: 'right',
        sortField: CampaignReportOrderField.COST_PER_LIKE
      },
      cell: ({ row }) => (
        <TableCell.Number value={formatPrice(row.original.stats.costsPerLike || null, currency)} unit={currency} />
      )
    },
    {
      accessorKey: 'costsPerComment',
      header: t<string>('HeaderColumn.Cost / Comment'),
      meta: {
        align: 'right',
        sortField: CampaignReportOrderField.COST_PER_COMMENT
      },
      cell: ({ row }) => (
        <TableCell.Number value={formatPrice(row.original.stats.costsPerComment || null, currency)} unit={currency} />
      )
    },
    {
      accessorKey: 'costsPerShare',
      header: t<string>('HeaderColumn.Cost / Share'),
      meta: {
        align: 'right',
        sortField: CampaignReportOrderField.COST_PER_SHARE
      },
      cell: ({ row }) => (
        <TableCell.Number value={formatPrice(row.original.stats.costsPerShare || null, currency)} unit={currency} />
      )
    },
    {
      accessorKey: 'costsPerSaved',
      header: t<string>('HeaderColumn.Cost / Save'),
      meta: {
        align: 'right',
        sortField: CampaignReportOrderField.COST_PER_SAVED
      },
      cell: ({ row }) => (
        <TableCell.Number value={formatPrice(row.original.stats.costsPerSaved || null, currency)} unit={currency} />
      )
    },
    {
      accessorKey: 'costsPerClick',
      header: t<string>('HeaderColumn.CPC'),
      meta: {
        align: 'right',
        help: t<string>('Tooltip.CPC Help'),
        sortField: CampaignReportOrderField.COST_PER_CLICK
      },
      cell: ({ row }) => (
        <TableCell.Number value={formatPrice(row.original.stats.costsPerClick || null, currency)} unit={currency} />
      )
    },
    {
      accessorKey: 'costsPerAction',
      header: t<string>('HeaderColumn.CPA'),
      meta: {
        align: 'right',
        help: t<string>('Tooltip.CPA Help'),
        sortField: CampaignReportOrderField.COST_PER_ACTION
      },
      cell: ({ row }) => (
        <TableCell.Number value={formatPrice(row.original.stats.costsPerAction || null, currency)} unit={currency} />
      )
    },
    {
      accessorKey: 'back',
      header: t<string>('HeaderColumn.Back'),
      meta: {
        align: 'right',
        help: t<string>('Tooltip.backHelp'),
        sortField: CampaignReportOrderField.BACK,
        hidden: isEngagement && !(hasThreads || hasIgStory)
      },
      cell: ({ row }) => <TableCell.Number value={formatIntNumber(row.original.stats.back)} />
    },
    {
      accessorKey: 'exited',
      header: t<string>('HeaderColumn.Exited'),
      meta: {
        align: 'right',
        help: t<string>('Tooltip.exitedHelp'),
        sortField: CampaignReportOrderField.EXITED,
        hidden: isEngagement && !hasIgOrThreads
      },
      cell: ({ row }) => <TableCell.Number value={formatIntNumber(row.original.stats.exited)} />
    },
    {
      accessorKey: 'forward',
      header: t<string>('HeaderColumn.Forward'),
      meta: {
        align: 'right',
        help: t<string>('Tooltip.forwardHelp'),
        sortField: CampaignReportOrderField.FORWARD
      },
      cell: ({ row }) => <TableCell.Number value={formatIntNumber(row.original.stats.forward)} />
    },
    {
      accessorKey: 'nextStory',
      header: t<string>('HeaderColumn.Next Story'),
      meta: {
        align: 'right',
        help: t<string>('Tooltip.nextStoryHelp'),
        sortField: CampaignReportOrderField.NEXT_STORY,
        hidden: isEngagement && !hasIgOrThreads
      },
      cell: ({ row }) => <TableCell.Number value={formatIntNumber(row.original.stats.nextStory)} />
    },
    {
      accessorKey: 'navigation',
      header: t<string>('HeaderColumn.Navigation'),
      meta: {
        align: 'right',
        help: t<string>('Tooltip.navigationHelp'),
        sortField: CampaignReportOrderField.NAVIGATION,
        hidden: isEngagement && !hasIgOrThreads
      },
      cell: ({ row }) => <TableCell.Number value={formatIntNumber(row.original.stats.navigation)} />
    },
    {
      accessorKey: 'accountsEngaged',
      header: t<string>('HeaderColumn.Accounts Engaged'),
      meta: {
        align: 'right',
        hidden: !isEngagement || !hasInstagram,
        help: t<string>('Tooltip.accountsEngagedHelp'),
        sortField: CampaignReportOrderField.ACCOUNTS_ENGAGED
      },
      cell: ({ row }) => <TableCell.Number value={formatIntNumber(row.original.stats.accountsEngaged || 0)} />
    },
    {
      accessorKey: 'profileActivity',
      header: t<string>(
        isEngagement && hasOnlyTwitter ? 'HeaderColumn.Profile access' : 'HeaderColumn.Profile Activity'
      ),
      meta: {
        align: 'right',
        help: t<string>('Tooltip.profileActivityHelp'),
        sortField: CampaignReportOrderField.PROFILE_ACTIVITY,
        hidden: isEngagement && !(hasIgOrThreads || hasTwitter)
      },
      cell: ({ row }) => <TableCell.Number value={formatIntNumber(row.original.stats.profileActivity)} />
    },
    {
      accessorKey: 'profileVisits',
      header: t<string>('HeaderColumn.Profile Visits'),
      meta: {
        align: 'right',
        hidden: !isEngagement || !(hasInstagram || hasTwitter),
        help: t<string>('Tooltip.profileVisitsHelp'),
        sortField: CampaignReportOrderField.PROFILE_VISITS
      },
      cell: ({ row }) => <TableCell.Number value={formatIntNumber(row.original.stats.profileVisits || 0)} />
    },
    {
      accessorKey: 'follows',
      header: t<string>('HeaderColumn.Follows'),
      meta: {
        align: 'right',
        hidden: !isEngagement || !hasInstagram,
        help: t<string>('Tooltip.followsHelp'),
        sortField: CampaignReportOrderField.FOLLOWS
      },
      cell: ({ row }) => <TableCell.Number value={formatIntNumber(row.original.stats.follows || 0)} />
    },
    {
      accessorKey: 'insightDataAcquisition',
      header: t<string>('HeaderColumn.Insight acquisition date'),
      meta: {
        help: t<string>('Tooltip.insightAcquisitionDateHelp'),
        hidden: !isEngagement
      },
      cell: ({ row }) => <TableCell.Date value={row.original.stats.insightDataAcquisition || null} />
    }
  ];

  return { columns };
};
